/**
 * Canvas principal do Flow Builder usando React Flow
 * Permite arrastar, conectar e editar nodes do fluxo visual
 * Suporta agrupamento de nodes em sub-flows visuais
 * 
 * CORREÇÃO v2: Sincronização de estado corrigida para garantir
 * que exclusões de nodes funcionem corretamente
 * RELEASE: 1.4.0 - 2026-10-02 - Usa catálogo visual único compartilhado com o Analytics.
 * RELEASE: 1.3.0 - 2026-03-07 - Adicionado suporte a agrupamento de nodes (GroupNode)
 */

import { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  BackgroundVariant,
  MiniMap,
  useNodesState,
  useEdgesState,
  useReactFlow,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Connection,
  ConnectionLineType,
  Panel,
  EdgeTypes,
  NodeChange,
  EdgeChange,
  OnSelectionChangeParams,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { FlowNode, FlowEdge } from '@/types/flows';
import { toast } from 'sonner';
import { ABTestStats } from '@/hooks/useABTestStats';
import { ABTestEdge } from './edges/ABTestEdge';
import { createLogger } from '@/lib/logger';
import { Button } from '@/components/ui/button';
import { Layers, X, AlertTriangle, Crosshair } from 'lucide-react';
import { FLOW_NODE_TYPES } from './nodes/flowNodeRegistry';

// Logger para debug do canvas
const log = createLogger('FlowCanvas');

interface FlowCanvasProps {
  initialNodes?: FlowNode[];
  initialEdges?: FlowEdge[];
  onNodesChange?: (nodes: FlowNode[]) => void;
  onEdgesChange?: (edges: FlowEdge[]) => void;
  onNodeClick?: (event: React.MouseEvent, node: FlowNode) => void;
  /** Callback para editar um node (abre modal de configuração) */
  onNodeEdit?: (nodeId: string, nodeType: string) => void;
  /** Callback para deletar um node */
  onNodeDelete?: (nodeId: string) => void;
  /** Callback para testar fluxo a partir de um node específico */
  onTestFromNode?: (nodeId: string) => void;
  highlightedNodeId?: string | null;
  /** IDs de nodes com problema (ex: desconectados ao publicar) - exibidos com borda vermelha pulsante */
  invalidNodeIds?: string[];
  /** Callback opcional para fechar o banner de nodes inválidos */
  onClearInvalidNodes?: () => void;
  abTestStats?: ABTestStats;
  /** 
   * Indica se algum modal/dialog está aberto
   * Quando true, desativa zoom/pan do canvas para permitir scroll nos modais
   */
  isDialogOpen?: boolean;
  /** Permissão para editar nodes (arrastar, conectar, editar config) */
  canEditFlow?: boolean;
  /** Permissão para deletar nodes */
  canDeleteFlow?: boolean;
}

// Padding ao redor dos nodes filhos dentro do grupo
const GROUP_PADDING = 40;

/**
 * Componente de canvas do flow builder
 * Gerencia nodes, edges e interações de arrastar/conectar
 */
export function FlowCanvas({
  initialNodes = [],
  initialEdges = [],
  onNodesChange,
  onEdgesChange,
  onNodeClick,
  onNodeEdit,
  onNodeDelete,
  onTestFromNode,
  highlightedNodeId,
  invalidNodeIds = [],
  onClearInvalidNodes,
  abTestStats = {},
  isDialogOpen = false,
  canEditFlow = true,
  canDeleteFlow = true,
}: FlowCanvasProps) {
  const [nodes, setNodes] = useNodesState<FlowNode>(initialNodes);
  const [edges, setEdges] = useEdgesState<FlowEdge>(initialEdges);
  
  // Estado para nodes selecionados (para toolbar de seleção múltipla)
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  
  // Estado do modo de edição de grupo (adicionar nodes a um grupo existente)
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  
  // Refs para evitar stale closures nos callbacks
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  
  // Mantém refs atualizadas
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);
  
  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);
  
  // Obtém função para converter coordenadas de tela para coordenadas do fluxo (considera zoom/pan)
  const { screenToFlowPosition, fitView } = useReactFlow();

  /**
   * Foca a viewport em um conjunto de nodes inválidos.
   * Usado pelos botões "Localizar" do banner de erro.
   */
  const focusOnInvalidNodes = useCallback((ids: string[]) => {
    if (!ids.length) return;
    fitView({
      nodes: ids.map(id => ({ id })),
      padding: 0.4,
      duration: 600,
      maxZoom: 1.2,
    });
  }, [fitView]);

  /**
   * CORREÇÃO: Sincroniza nodes/edges quando initialNodes/initialEdges mudam
   */
  useEffect(() => {
    log.debug('Sincronizando nodes do initialNodes', { 
      initialCount: initialNodes.length,
      currentCount: nodes.length 
    });
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);
  
  useEffect(() => {
    log.debug('Sincronizando edges do initialEdges', { 
      initialCount: initialEdges.length,
      currentCount: edges.length 
    });
    setEdges(initialEdges);
  }, [initialEdges, setEdges]);

  /** Catálogo compartilhado evita divergência visual entre construtor e Analytics. */
  const nodeTypes = FLOW_NODE_TYPES;

  /**
   * Mapeamento de tipos de edge para componentes customizados
   */
  const edgeTypes: EdgeTypes = useMemo(() => ({
    abtest: ABTestEdge,
  }), []);

  /**
   * Callback quando usuário conecta dois nodes
   */
  const onConnect = useCallback(
    (params: Connection) => {
      const sourceNode = nodesRef.current.find(n => n.id === params.source);
      const isABTestSource = sourceNode?.data.type === 'ab_test';
      
      const newEdges = addEdge(
        {
          ...params,
          type: isABTestSource ? 'abtest' : 'default', // Bezier para melhor visualização das conexões
          animated: true,
        },
        edgesRef.current
      );
      setEdges(newEdges);
      onEdgesChange?.(newEdges);
    },
    [setEdges, onEdgesChange]
  );

  /**
   * Handler de mudanças de nodes usando applyNodeChanges
   * Protege node de Início contra deleção
   */
  const handleNodesChangeInternal = useCallback(
    (changes: NodeChange<FlowNode>[]) => {
      const filteredChanges = changes.filter((change) => {
        if (change.type === 'remove') {
          const nodeToRemove = nodesRef.current.find(n => n.id === change.id);
          if (nodeToRemove?.data.type === 'start') {
            toast.error('O node de Início não pode ser removido', {
              description: 'Todo fluxo deve ter um ponto de entrada'
            });
            return false;
          }
        }
        return true;
      });

      const nextNodes = applyNodeChanges(filteredChanges, nodesRef.current) as FlowNode[];
      
      log.debug('handleNodesChangeInternal', { 
        changeTypes: filteredChanges.map(c => c.type),
        beforeCount: nodesRef.current.length,
        afterCount: nextNodes.length
      });
      
      setNodes(nextNodes);
      onNodesChange?.(nextNodes);
    },
    [setNodes, onNodesChange]
  );

  /**
   * Handler de mudanças de edges usando applyEdgeChanges
   */
  const handleEdgesChangeInternal = useCallback(
    (changes: EdgeChange<FlowEdge>[]) => {
      const nextEdges = applyEdgeChanges(changes, edgesRef.current) as FlowEdge[];
      
      log.debug('handleEdgesChangeInternal', { 
        changeTypes: changes.map(c => c.type),
        beforeCount: edgesRef.current.length,
        afterCount: nextEdges.length
      });
      
      setEdges(nextEdges);
      onEdgesChange?.(nextEdges);
    },
    [setEdges, onEdgesChange]
  );

  /**
   * Permite drop de novos nodes no canvas
   */
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  /**
   * Retorna configuração padrão específica por tipo de node
   */
  const getDefaultConfig = (nodeType: string) => {
    switch (nodeType) {
      case 'ab_test':
        return {
          variants: [
            { id: 'variant_0', label: 'A', percentage: 50 },
            { id: 'variant_1', label: 'B', percentage: 50 },
          ],
        };
      default:
        return {};
    }
  };

  /**
   * Cria novo node quando usuário solta bloco no canvas
   */
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      if (!type) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode: FlowNode = {
        id: `${type}-${Date.now()}`,
        type: type as any,
        position,
        data: {
          type: type as any,
          label: type.replace(/_/g, ' ').toUpperCase(),
          config: getDefaultConfig(type),
        },
      };

      const nextNodes = [...nodesRef.current, newNode];
      
      log.debug('onDrop - novo node criado', { 
        nodeId: newNode.id, 
        nodeType: type,
        totalNodes: nextNodes.length 
      });
      
      setNodes(nextNodes);
      onNodesChange?.(nextNodes);
    },
    [setNodes, onNodesChange, screenToFlowPosition]
  );

  /**
   * Função de exclusão interna que remove node + edges conectadas
   */
  const handleDeleteNode = useCallback((nodeId: string) => {
    log.debug('handleDeleteNode iniciado', { nodeId });
    
    const nodeToDelete = nodesRef.current.find(n => n.id === nodeId);
    
    if (nodeToDelete?.data.type === 'start' || nodeToDelete?.data.type === 'end') {
      log.warn('Tentativa de excluir node protegido', { nodeId, type: nodeToDelete?.data.type });
      toast.warning('Nodes de início e fim não podem ser excluídos');
      return;
    }
    
    const nextNodes = nodesRef.current.filter(n => n.id !== nodeId);
    const nextEdges = edgesRef.current.filter(
      e => e.source !== nodeId && e.target !== nodeId
    );
    
    log.debug('handleDeleteNode concluído', { 
      nodeId,
      nodesAntes: nodesRef.current.length,
      nodesDepois: nextNodes.length,
      edgesAntes: edgesRef.current.length,
      edgesDepois: nextEdges.length
    });
    
    setNodes(nextNodes);
    setEdges(nextEdges);
    onNodesChange?.(nextNodes);
    onEdgesChange?.(nextEdges);
    
    toast.success('Node excluído com sucesso');
  }, [setNodes, setEdges, onNodesChange, onEdgesChange]);

  /**
   * Duplica um node existente com 100% das configurações
   */
  const handleDuplicateNode = useCallback((nodeId: string) => {
    log.debug('handleDuplicateNode iniciado', { nodeId });
    
    const originalNode = nodesRef.current.find(n => n.id === nodeId);
    
    if (!originalNode) {
      log.warn('Node não encontrado para duplicação', { nodeId });
      toast.error('Node não encontrado');
      return;
    }
    
    if (originalNode.data.type === 'start' || originalNode.data.type === 'end') {
      log.warn('Tentativa de duplicar node protegido', { nodeId, type: originalNode.data.type });
      toast.warning('Nodes de início e fim não podem ser duplicados');
      return;
    }
    
    const newId = `${originalNode.data.type}-${crypto.randomUUID().substring(0, 8)}-${Date.now()}`;
    
    const clonedConfig = originalNode.data.config 
      ? JSON.parse(JSON.stringify(originalNode.data.config))
      : {};
    
    if (clonedConfig.nickname) {
      clonedConfig.nickname = `${clonedConfig.nickname} (cópia)`;
    }
    
    const newNode: FlowNode = {
      id: newId,
      type: originalNode.type,
      position: {
        x: (originalNode.position?.x || 0) + 50,
        y: (originalNode.position?.y || 0) + 80,
      },
      data: {
        ...originalNode.data,
        config: clonedConfig,
      },
    };
    
    const nextNodes = [...nodesRef.current, newNode];
    
    log.debug('handleDuplicateNode concluído', { 
      originalId: nodeId,
      newId,
      nodeType: originalNode.data.type,
      totalNodes: nextNodes.length
    });
    
    setNodes(nextNodes);
    onNodesChange?.(nextNodes);
    
    toast.success('Node duplicado com sucesso');
  }, [setNodes, onNodesChange]);

  /**
   * Duplica um grupo completo: grupo + todos os nodes filhos + edges internas
   * Cria cópias com novos IDs e mantém as conexões internas entre os filhos
   */
  const handleDuplicateGroup = useCallback((groupId: string) => {
    log.debug('handleDuplicateGroup iniciado', { groupId });

    const groupNode = nodesRef.current.find(n => n.id === groupId);
    if (!groupNode) {
      log.warn('Grupo não encontrado para duplicação', { groupId });
      toast.error('Grupo não encontrado');
      return;
    }

    // Encontra todos os nodes filhos do grupo
    const childNodes = nodesRef.current.filter(n => n.parentId === groupId);

    // Mapa de IDs antigos → novos (grupo + filhos)
    const newGroupId = `group-${crypto.randomUUID().substring(0, 8)}-${Date.now()}`;
    const idMap: Record<string, string> = { [groupId]: newGroupId };

    // Gera novos IDs para cada filho
    childNodes.forEach(child => {
      idMap[child.id] = `${child.data.type}-${crypto.randomUUID().substring(0, 8)}-${Date.now()}`;
    });

    // Clona o grupo com posição deslocada e nome " (cópia)"
    const clonedGroupConfig = groupNode.data.config
      ? JSON.parse(JSON.stringify(groupNode.data.config))
      : {};
    const originalGroupName = clonedGroupConfig.groupName || 'Grupo';
    clonedGroupConfig.groupName = `${originalGroupName} (cópia)`;

    const newGroup: FlowNode = {
      id: newGroupId,
      type: groupNode.type,
      position: {
        x: (groupNode.position?.x || 0) + 100,
        y: (groupNode.position?.y || 0) + 100,
      },
      data: {
        ...groupNode.data,
        config: clonedGroupConfig,
      },
      style: groupNode.style ? { ...groupNode.style } : undefined,
    };

    // Clona cada node filho mantendo posição relativa e novo parentId
    const newChildren: FlowNode[] = childNodes.map(child => {
      const clonedConfig = child.data.config
        ? JSON.parse(JSON.stringify(child.data.config))
        : {};
      if (clonedConfig.nickname) {
        clonedConfig.nickname = `${clonedConfig.nickname} (cópia)`;
      }

      return {
        id: idMap[child.id],
        type: child.type,
        position: { ...child.position },
        parentId: newGroupId,
        extent: child.extent,
        data: {
          ...child.data,
          config: clonedConfig,
        },
      } as FlowNode;
    });

    // Clona edges internas (onde source E target pertencem ao grupo original)
    const currentEdges = edgesRef.current;
    const childIdSet = new Set(Object.keys(idMap));
    const newEdges = currentEdges
      .filter(edge => childIdSet.has(edge.source) && childIdSet.has(edge.target))
      .map(edge => ({
        ...edge,
        id: `edge-${crypto.randomUUID().substring(0, 8)}`,
        source: idMap[edge.source],
        target: idMap[edge.target],
      }));

    // Atualiza estado com novos nodes e edges
    const nextNodes = [...nodesRef.current, newGroup, ...newChildren];
    const nextEdges = [...currentEdges, ...newEdges];

    log.debug('handleDuplicateGroup concluído', {
      originalGroupId: groupId,
      newGroupId,
      childrenCount: newChildren.length,
      edgesCount: newEdges.length,
    });

    setNodes(nextNodes);
    setEdges(nextEdges);
    onNodesChange?.(nextNodes);
    onEdgesChange?.(nextEdges);

    toast.success(`Grupo duplicado com ${newChildren.length} nodes`);
  }, [setNodes, setEdges, onNodesChange, onEdgesChange]);

  // ========================================
  // AGRUPAMENTO DE NODES (SUB-FLOWS)
  // ========================================

  /**
   * Detecta mudança na seleção de nodes
   * Filtra nodes protegidos e nodes de grupo para toolbar de seleção
   */
  const handleSelectionChange = useCallback((params: OnSelectionChangeParams) => {
    const selectedIds = params.nodes
      .filter(n => {
        const nodeType = (n as FlowNode).data?.type || n.type;
        // Exclui nodes protegidos e grupos da seleção para agrupar
        return nodeType !== 'start' && nodeType !== 'end' && nodeType !== 'group';
      })
      .map(n => n.id);
    setSelectedNodeIds(selectedIds);
  }, []);

  /**
   * Agrupa os nodes selecionados em um container visual
   * Calcula bounding box, cria grupo e reparenta os filhos
   */
  const handleGroupNodes = useCallback(() => {
    if (selectedNodeIds.length < 2) return;

    const currentNodes = nodesRef.current;
    const nodesToGroup = currentNodes.filter(n => selectedNodeIds.includes(n.id));

    if (nodesToGroup.length < 2) return;

    // Calcula bounding box dos nodes selecionados
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    nodesToGroup.forEach(node => {
      const x = node.position?.x || 0;
      const y = node.position?.y || 0;
      // Estimativa de largura/altura do node (React Flow não expõe diretamente)
      const nodeWidth = (node.measured?.width as number) || (node.style?.width as number) || 200;
      const nodeHeight = (node.measured?.height as number) || (node.style?.height as number) || 80;

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + nodeWidth);
      maxY = Math.max(maxY, y + nodeHeight);
    });

    // Adiciona padding ao redor
    const groupX = minX - GROUP_PADDING;
    const groupY = minY - GROUP_PADDING - 30; // Extra para header
    const groupWidth = (maxX - minX) + GROUP_PADDING * 2;
    const groupHeight = (maxY - minY) + GROUP_PADDING * 2 + 30; // Extra para header

    // Cria node do grupo
    const groupId = `group-${Date.now()}`;
    const groupNode: FlowNode = {
      id: groupId,
      type: 'group',
      position: { x: groupX, y: groupY },
      data: {
        type: 'group',
        label: 'Grupo',
        config: { groupName: 'Grupo' },
      },
      style: {
        width: groupWidth,
        height: groupHeight,
        zIndex: -1,
      },
    };

    // Atualiza posições dos filhos para serem relativas ao grupo
    const updatedNodes = currentNodes.map(node => {
      if (selectedNodeIds.includes(node.id)) {
        return {
          ...node,
          parentId: groupId,
          extent: 'parent' as const,
          position: {
            x: (node.position?.x || 0) - groupX,
            y: (node.position?.y || 0) - groupY,
          },
        };
      }
      return node;
    });

    // Insere grupo ANTES dos filhos para garantir renderização correta
    const nextNodes = [groupNode, ...updatedNodes];

    log.debug('handleGroupNodes concluído', {
      groupId,
      childCount: selectedNodeIds.length,
      bounds: { groupX, groupY, groupWidth, groupHeight }
    });

    setNodes(nextNodes);
    onNodesChange?.(nextNodes);
    setSelectedNodeIds([]);
    
    toast.success(`${selectedNodeIds.length} nodes agrupados`);
  }, [selectedNodeIds, setNodes, onNodesChange]);

  /**
   * Desagrupa os nodes filhos de um grupo
   * Converte posições relativas de volta para absolutas e remove o grupo
   */
  const handleUngroupNodes = useCallback((groupId: string) => {
    const currentNodes = nodesRef.current;
    const groupNode = currentNodes.find(n => n.id === groupId);

    if (!groupNode) return;

    const groupX = groupNode.position?.x || 0;
    const groupY = groupNode.position?.y || 0;

    // Restaura posições absolutas dos filhos e remove parentId
    const updatedNodes = currentNodes
      .filter(n => n.id !== groupId) // Remove o grupo
      .map(node => {
        if (node.parentId === groupId) {
          // Remove parentId e extent, converte posição para absoluta
          const { parentId, extent, ...rest } = node;
          return {
            ...rest,
            position: {
              x: (node.position?.x || 0) + groupX,
              y: (node.position?.y || 0) + groupY,
            },
          } as FlowNode;
        }
        return node;
      });

    log.debug('handleUngroupNodes concluído', {
      groupId,
      nodesAntes: currentNodes.length,
      nodesDepois: updatedNodes.length,
    });

    setNodes(updatedNodes);
    onNodesChange?.(updatedNodes);
    
    toast.success('Nodes desagrupados');
  }, [setNodes, onNodesChange]);

  /**
   * Exclui grupo mas mantém os filhos (desagrupa + remove container)
   */
  const handleDeleteGroup = useCallback((groupId: string) => {
    // Desagrupar é a mesma operação de excluir grupo (mantém filhos)
    handleUngroupNodes(groupId);
  }, [handleUngroupNodes]);

  /**
   * Renomeia um grupo alterando o config.groupName
   */
  const handleRenameGroup = useCallback((groupId: string, newName: string) => {
    const currentNodes = nodesRef.current;
    const nextNodes = currentNodes.map(node => {
      if (node.id === groupId) {
        return {
          ...node,
          data: {
            ...node.data,
            config: {
              ...(node.data.config || {}),
              groupName: newName,
            },
          },
        };
      }
      return node;
    });

    setNodes(nextNodes);
    onNodesChange?.(nextNodes);
  }, [setNodes, onNodesChange]);

  // ========================================
  // MODO DE EDIÇÃO DE GRUPO (ADICIONAR NODES)
  // ========================================

  /**
   * Ativa/desativa o modo de edição de grupo
   * Permite clicar em nodes para adicioná-los ao grupo
   */
  const handleEnterGroupEditMode = useCallback((groupId: string) => {
    setEditingGroupId(prev => prev === groupId ? null : groupId);
    log.debug('Modo edição de grupo', { groupId, ativo: true });
  }, []);

  /**
   * Adiciona um node externo a um grupo existente
   * Recalcula posição relativa e expande grupo se necessário
   */
  const handleAddNodeToGroup = useCallback((nodeId: string, groupId: string) => {
    const currentNodes = nodesRef.current;
    const targetNode = currentNodes.find(n => n.id === nodeId);
    const groupNode = currentNodes.find(n => n.id === groupId);

    if (!targetNode || !groupNode) return;

    // Não permite adicionar nodes protegidos ou outros grupos
    if (targetNode.data.type === 'start' || targetNode.data.type === 'end' || targetNode.data.type === 'group') {
      toast.warning('Este tipo de node não pode ser adicionado ao grupo');
      return;
    }

    // Toggle: se já pertence ao grupo, remove-o
    if (targetNode.parentId === groupId) {
      const groupX = groupNode.position?.x || 0;
      const groupY = groupNode.position?.y || 0;
      const nodeRelX = targetNode.position?.x || 0;
      const nodeRelY = targetNode.position?.y || 0;

      // Recalcula posição absoluta do node
      const absoluteX = groupX + nodeRelX;
      const absoluteY = groupY + nodeRelY;

      const nextNodes = currentNodes.map(node => {
        if (node.id === nodeId) {
          // Remove parentId e extent, restaura posição absoluta
          const { parentId, extent, ...rest } = node as any;
          return {
            ...rest,
            position: { x: absoluteX, y: absoluteY },
          };
        }
        return node;
      });

      log.debug('Node removido do grupo', { nodeId, groupId, absolutePosition: { x: absoluteX, y: absoluteY } });
      setNodes(nextNodes);
      onNodesChange?.(nextNodes);
      toast.success('Node removido do grupo');
      return;
    }

    // Não permite adicionar node que pertence a outro grupo (precisa desagrupar primeiro)
    if (targetNode.parentId) {
      toast.warning('Node já pertence a outro grupo. Desagrupe-o primeiro.');
      return;
    }

    const groupX = groupNode.position?.x || 0;
    const groupY = groupNode.position?.y || 0;
    const nodeX = targetNode.position?.x || 0;
    const nodeY = targetNode.position?.y || 0;

    // Calcula posição relativa ao grupo
    const relativeX = nodeX - groupX;
    const relativeY = nodeY - groupY;

    // Calcula novo bounding box incluindo o node adicionado
    const nodeWidth = (targetNode.measured?.width as number) || (targetNode.style?.width as number) || 200;
    const nodeHeight = (targetNode.measured?.height as number) || (targetNode.style?.height as number) || 80;
    const currentGroupWidth = (groupNode.style?.width as number) || 250;
    const currentGroupHeight = (groupNode.style?.height as number) || 150;

    // Expande grupo se o node ficar fora dos limites
    const needsExpansionRight = relativeX + nodeWidth + GROUP_PADDING > currentGroupWidth;
    const needsExpansionBottom = relativeY + nodeHeight + GROUP_PADDING > currentGroupHeight;
    const needsExpansionLeft = relativeX < GROUP_PADDING;
    const needsExpansionTop = relativeY < GROUP_PADDING + 30; // 30 = header

    let newGroupX = groupX;
    let newGroupY = groupY;
    let newGroupWidth = currentGroupWidth;
    let newGroupHeight = currentGroupHeight;
    let deltaX = 0;
    let deltaY = 0;

    if (needsExpansionLeft) {
      const expansion = GROUP_PADDING - relativeX;
      newGroupX -= expansion;
      newGroupWidth += expansion;
      deltaX = expansion; // Filhos existentes precisam ajustar posição
    }
    if (needsExpansionTop) {
      const expansion = (GROUP_PADDING + 30) - relativeY;
      newGroupY -= expansion;
      newGroupHeight += expansion;
      deltaY = expansion;
    }
    if (needsExpansionRight) {
      newGroupWidth = relativeX + nodeWidth + GROUP_PADDING + deltaX;
    }
    if (needsExpansionBottom) {
      newGroupHeight = relativeY + nodeHeight + GROUP_PADDING + deltaY;
    }

    const nextNodes = currentNodes.map(node => {
      // Atualiza o grupo com novo tamanho/posição
      if (node.id === groupId) {
        return {
          ...node,
          position: { x: newGroupX, y: newGroupY },
          style: {
            ...node.style,
            width: newGroupWidth,
            height: newGroupHeight,
          },
        };
      }
      // Move o node para dentro do grupo
      if (node.id === nodeId) {
        return {
          ...node,
          parentId: groupId,
          extent: 'parent' as const,
          position: {
            x: relativeX + deltaX,
            y: relativeY + deltaY,
          },
        };
      }
      // Ajusta posição dos filhos existentes se o grupo expandiu para esquerda/cima
      if (node.parentId === groupId && (deltaX > 0 || deltaY > 0)) {
        return {
          ...node,
          position: {
            x: (node.position?.x || 0) + deltaX,
            y: (node.position?.y || 0) + deltaY,
          },
        };
      }
      return node;
    });

    log.debug('handleAddNodeToGroup concluído', {
      nodeId,
      groupId,
      relativePosition: { x: relativeX + deltaX, y: relativeY + deltaY },
      groupExpanded: needsExpansionLeft || needsExpansionRight || needsExpansionTop || needsExpansionBottom,
    });

    setNodes(nextNodes);
    onNodesChange?.(nextNodes);
    toast.success('Node adicionado ao grupo');
  }, [setNodes, onNodesChange]);

  /**
   * Listener ESC para sair do modo de edição de grupo
   */
  useEffect(() => {
    if (!editingGroupId) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setEditingGroupId(null);
        log.debug('Saiu do modo edição de grupo via ESC');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingGroupId]);

  /**
   * Handler de clique em node - intercepta quando em modo de edição de grupo
   */
  const handleNodeClick = useCallback((event: React.MouseEvent, node: FlowNode) => {
    // Se está em modo de edição de grupo, adiciona o node clicado ao grupo
    if (editingGroupId && node.id !== editingGroupId) {
      event.stopPropagation();
      handleAddNodeToGroup(node.id, editingGroupId);
      return;
    }
    // Comportamento padrão
    onNodeClick?.(event, node);
  }, [editingGroupId, handleAddNodeToGroup, onNodeClick]);

  /**
   * Processa nodes para injetar edges, callbacks e highlight
   * Inclui dados extras para nodes de grupo (contagem de filhos, callbacks)
   */
  const processedNodes = useMemo(() => {
    // Conta filhos por grupo para exibir no badge
    const childCountByGroup: Record<string, number> = {};
    nodes.forEach(node => {
      if (node.parentId) {
        childCountByGroup[node.parentId] = (childCountByGroup[node.parentId] || 0) + 1;
      }
    });

    return nodes.map((node) => {
      // Dados extras para nodes de grupo
      const isGroup = node.data.type === 'group';

      return {
        ...node,
        data: {
          ...node.data,
          edges,
          // Callbacks padrão para toolbar (condicionados às permissões)
          onEdit: canEditFlow && !isGroup ? () => onNodeEdit?.(node.id, node.data.type) : undefined,
          onDuplicate: canEditFlow && !isGroup ? () => handleDuplicateNode(node.id) : undefined,
          onDelete: canDeleteFlow && !isGroup ? () => handleDeleteNode(node.id) : undefined,
          onTestFromHere: onTestFromNode && !isGroup ? () => onTestFromNode(node.id) : undefined,
          // Callbacks exclusivos para nodes de grupo
          ...(isGroup ? {
            childCount: childCountByGroup[node.id] || 0,
            onUngroup: () => handleUngroupNodes(node.id),
            onDeleteGroup: () => handleDeleteGroup(node.id),
            onRenameGroup: (newName: string) => handleRenameGroup(node.id, newName),
            onEnterEditMode: () => handleEnterGroupEditMode(node.id),
            onDuplicateGroup: canEditFlow ? () => handleDuplicateGroup(node.id) : undefined,
            isEditingMode: editingGroupId === node.id,
          } : {}),
        },
        className: [
          node.id === highlightedNodeId ? 'flow-node-highlighted' : '',
          invalidNodeIds.includes(node.id) ? 'flow-node-error' : '',
        ].filter(Boolean).join(' '),
      };
    });
  }, [nodes, edges, highlightedNodeId, invalidNodeIds, onNodeEdit, handleDeleteNode, handleDuplicateNode, onTestFromNode, canEditFlow, canDeleteFlow, handleUngroupNodes, handleDeleteGroup, handleRenameGroup, handleEnterGroupEditMode, handleDuplicateGroup, editingGroupId]);

  /**
   * Injeta estatísticas de A/B test nas edges
   */
  const processedEdges = useMemo(() => {
    return edges.map((edge) => {
      const sourceNode = nodes.find(n => n.id === edge.source);
      if (sourceNode?.data.type !== 'ab_test') {
        // Força tipo bezier (default) para todas as edges existentes que possam ter tipo antigo (smoothstep)
        return edge.type !== 'default' ? { ...edge, type: 'default' } : edge;
      }
      
      const nodeStats = abTestStats[edge.source];
      if (!nodeStats) {
        return { ...edge, type: 'abtest' };
      }
      
      const variantIndex = parseInt(edge.sourceHandle?.replace('variant_', '') || '0');
      const variantLabels = ['A', 'B', 'C', 'D'];
      const variantLabel = variantLabels[variantIndex] || `V${variantIndex}`;
      const variantStats = nodeStats.variants[variantLabel];
      
      return {
        ...edge,
        type: 'abtest',
        data: {
          variant: variantLabel,
          count: variantStats?.count || 0,
          percentage: variantStats?.percentage || 0,
          total: nodeStats.total,
        },
      };
    });
  }, [edges, nodes, abTestStats]);

  // Verifica se há nodes suficientes selecionados para agrupar
  const canGroupSelection = selectedNodeIds.length >= 2 && canEditFlow;

  return (
    <div className="w-full h-full">
      <ReactFlow
        nodes={processedNodes}
        edges={processedEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={handleNodesChangeInternal}
        onEdgesChange={handleEdgesChangeInternal}
        onConnect={onConnect}
        onNodeClick={handleNodeClick as any}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onSelectionChange={handleSelectionChange}
        connectionLineType={ConnectionLineType.Bezier} // Curva Bezier para preview da conexão
        fitView
        fitViewOptions={{
          maxZoom: 0.75,
          padding: 0.2,
        }}
        minZoom={0.25}
        maxZoom={1.5}
        snapToGrid
        snapGrid={[15, 15]}
        className="bg-background"
        deleteKeyCode="Delete"
        multiSelectionKeyCode="Control"
        zoomOnScroll={!isDialogOpen}
        panOnScroll={!isDialogOpen}
        preventScrolling={!isDialogOpen}
      >
        <Controls className="bg-card border border-border rounded-lg" />
        <Background 
          variant={BackgroundVariant.Dots} 
          gap={12} 
          size={1} 
          color="hsl(var(--border))" 
        />
        <Panel position="top-left" className="bg-card/80 backdrop-blur-sm border border-border rounded-lg p-2">
          <p className="text-xs text-muted-foreground">
            Arraste blocos da sidebar para o canvas
          </p>
        </Panel>

        {/* ========================================================
            Banner de NODES INVÁLIDOS (após tentativa de publicação)
            Lista cada node problemático com botão "Localizar" para
            centralizar a viewport sobre ele. Some automaticamente
            quando todos os nodes são corrigidos (auto-clear no hook).
        ======================================================== */}
        {invalidNodeIds.length > 0 && (
          <Panel
            position="top-center"
            className="animate-in fade-in-0 slide-in-from-top-2 duration-200 max-w-[640px] w-[90vw]"
          >
            <div className="flex flex-col gap-2 bg-destructive/10 border border-destructive/40 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span className="text-sm font-semibold">
                    {invalidNodeIds.length} node(s) com problema
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs border-destructive/40 text-destructive hover:bg-destructive/10"
                    onClick={() => focusOnInvalidNodes(invalidNodeIds)}
                  >
                    <Crosshair className="h-3 w-3 mr-1" />
                    Localizar todos
                  </Button>
                  {onClearInvalidNodes && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive hover:bg-destructive/10"
                      onClick={onClearInvalidNodes}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Chips clicáveis dos nodes problemáticos (até 8 visíveis) */}
              <div className="flex flex-wrap gap-1.5">
                {invalidNodeIds.slice(0, 8).map(id => {
                  const node = nodes.find(n => n.id === id);
                  const label = (node?.data?.label as string) || node?.data?.type || id.slice(0, 6);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => focusOnInvalidNodes([id])}
                      className="text-xs px-2 py-0.5 rounded-md bg-destructive/15 hover:bg-destructive/25 text-destructive border border-destructive/30 transition-colors max-w-[180px] truncate"
                      title={`Localizar "${label}"`}
                    >
                      {label}
                    </button>
                  );
                })}
                {invalidNodeIds.length > 8 && (
                  <span className="text-xs text-destructive/70 px-1 py-0.5">
                    +{invalidNodeIds.length - 8} outros
                  </span>
                )}
              </div>
            </div>
          </Panel>
        )}

        {/* Banner do modo de edição de grupo */}
        {editingGroupId && (
          <Panel position="top-center" className="animate-in fade-in-0 slide-in-from-top-2 duration-200">
            <div className="flex items-center gap-3 bg-primary text-primary-foreground rounded-lg px-4 py-2 shadow-lg">
              <Layers className="h-4 w-4" />
              <span className="text-sm font-medium">
                Modo edição: clique para adicionar/remover nodes do grupo
              </span>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 hover:bg-primary-foreground/20 text-primary-foreground"
                onClick={() => setEditingGroupId(null)}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </Panel>
        )}

        {/* Toolbar de seleção múltipla - Botão Agrupar */}
        {canGroupSelection && !editingGroupId && (
          <Panel position="top-center" className="animate-in fade-in-0 slide-in-from-top-2 duration-200">
            <Button
              size="sm"
              variant="outline"
              className="gap-2 bg-card shadow-lg border-border"
              onClick={handleGroupNodes}
            >
              <Layers className="h-4 w-4" />
              Agrupar {selectedNodeIds.length} nodes
            </Button>
          </Panel>
        )}
        
        {/* MiniMap com cores por tipo de node */}
        <MiniMap
          nodeColor={(node) => {
            const type = (node as FlowNode).data?.type || node.type;
            switch (type) {
              case 'start': return '#22c55e';
              case 'end': return '#ef4444';
              case 'message_text':
              case 'message_media': return '#3b82f6';
              case 'message_carousel': return '#f59e0b';
              case 'question_buttons':
              case 'question_list': return '#8b5cf6';
              case 'condition': return '#f59e0b';
              case 'delay': return '#64748b';
              case 'input_data': return '#06b6d4';
              case 'add_tag':
              case 'update_contact':
              case 'transfer_human':
              case 'transfer_department': return '#10b981';
              case 'assign_phase': return '#14b8a6';
              case 'webhook': return '#6366f1';
              case 'goto': return '#ec4899';
              case 'ab_test': return '#f472b6';
              case 'set_variable': return '#14b8a6';
              case 'filter_array': return '#f59e0b';
              case 'format_array': return '#10b981';
              case 'trigger_check': return '#f59e0b';
              case 'business_hours': return '#2563eb';
              case 'crm_assign_seller': return '#a78bfa';
              case 'crm_create_activity': return '#8b5cf6';
              case 'task_create':
              case 'task_change_phase':
              case 'task_assign': return '#f59e0b'; // amber-500
              case 'group': return '#8b5cf6'; // Lilás para grupos
              case 'embed': return '#0ea5e9'; // Sky para embed
              default: return '#94a3b8';
            }
          }}
          nodeStrokeWidth={3}
          zoomable
          pannable
          className="bg-card/90 backdrop-blur-sm border border-border rounded-lg"
          style={{ width: 180, height: 120 }}
          maskColor="rgba(0, 0, 0, 0.15)"
        />
      </ReactFlow>
    </div>
  );
}
